import { useMutation, useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'

export interface Wallet {
  id: string
  user_id: string
  balance: number
  updated_at: string
}

export type CreditTransactionKind =
  | 'signup_bonus'
  | 'purchase'
  | 'grant'
  | 'debit'
  | 'refund'
  | 'adjustment'

export interface CreditTransaction {
  id: string
  amount: number
  balance_after: number
  kind: CreditTransactionKind
  reference_type: string | null
  reference_id: string | null
  description: string | null
  created_at: string
}

export interface TransactionListResponse {
  items: CreditTransaction[]
  total: number
}

export interface CreditPack {
  id: string
  name: string
  gp: number
  price_cents: number
  badge: string | null
  estimated_videos: number
  available: boolean
}

interface CheckoutResponse {
  pack_id: string
  plan_id: string
  checkout_url: string
}

const WALLET_KEY = ['wallet'] as const

export function useWalletQuery() {
  return useQuery({
    queryKey: [...WALLET_KEY, 'balance'],
    queryFn: async () => {
      const response = await apiClient.get<Wallet>('/wallet')
      return response.data
    },
    staleTime: 30_000,
  })
}

export function useTransactionsQuery(params?: {
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: [...WALLET_KEY, 'transactions', params],
    queryFn: async () => {
      const response = await apiClient.get<TransactionListResponse>(
        '/wallet/transactions',
        { params },
      )
      return response.data
    },
  })
}

export function useCreditPacksQuery() {
  return useQuery({
    queryKey: ['payments', 'packs'],
    queryFn: async () => {
      const response = await apiClient.get<{ packs: CreditPack[] }>(
        '/payments/packs',
      )
      return response.data.packs
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useCheckoutMutation() {
  return useMutation({
    mutationFn: async (packId: string) => {
      const response = await apiClient.post<CheckoutResponse>(
        '/payments/checkout',
        { pack_id: packId },
      )
      return response.data
    },
    onSuccess: (data) => {
      window.location.href = data.checkout_url
    },
  })
}
